"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Treatment } from "@/lib/treatments-data";
import Header from "@/components/layout/header";
import Breadcrumbs from "@/components/Breadcrumbs";
import HeroSection from "./dental-implants/hero-section";
import WhyChooseImplantsSection from "./dental-implants/why-choose-implants-section";
import WhyIstanbulSection from "./dental-implants/why-istanbul-section";
import ProcessStepsSection from "./dental-implants/process-steps-section";
import EligibilitySection from "./dental-implants/eligibility-section";
import CostPackagesSection from "./dental-implants/cost-packages-section";
import BoneGraftingSection from "./dental-implants/bone-grafting-section";
import SurgeryCareSection from "./dental-implants/surgery-care-section";
import FinalCTASection from "./shared/final-cta-section";
import StickyNavigation from "./shared/sticky-navigation";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useTranslation } from "@/hooks/useTranslation";

gsap.registerPlugin(ScrollTrigger);

interface DentalImplantsPageProps {
  treatment: Treatment;
}

export default function DentalImplantsPage({
  treatment,
}: DentalImplantsPageProps) {
  const { t, locale } = useTranslation('dental_implants');
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize GSAP ScrollTrigger
    ScrollTrigger.refresh();

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const sections = [
    { id: "hero", label: t('navigation.home') },
    { id: "why-implants", label: t('navigation.why_implants') },
    { id: "why-istanbul", label: t('navigation.why_istanbul') },
    { id: "process", label: t('navigation.process') },
    { id: "eligibility", label: t('navigation.eligibility') },
    { id: "cost", label: t('navigation.pricing') },
    { id: "bone-grafting", label: t('navigation.bone_grafting') },
    { id: "care", label: t('navigation.care') },
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-slate-50 dark:bg-navy-950 overflow-x-clip">
      <Header />

      {/* Treatment Header Section */}
      <section className="relative bg-gradient-to-b from-navy to-navy-950 border-b border-white/10 pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,rgba(210,161,56,0.08)_0%,transparent_60%)]" />
        <div className="container relative py-6">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-between mb-4">
            <Breadcrumbs />
            <Link
              href={`/${locale}/treatments/`}
              className="flex items-center space-x-2 text-white/60 hover:text-gold transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('navigation.back_to_treatments')}</span>
            </Link>
          </div>
        </div>
      </section>

      <StickyNavigation sections={sections} />

      <main className="relative">
        <HeroSection treatment={treatment} />
        <WhyChooseImplantsSection />
        <WhyIstanbulSection />
        <ProcessStepsSection />
        <EligibilitySection />
        <CostPackagesSection />
        <BoneGraftingSection />
        <SurgeryCareSection />
        <FinalCTASection
          treatmentName={t('cta.treatment_name')}
          treatmentKeyword="dental implants"
          scrollToSection="care"
          customHeading={
            <>
              {t('cta.custom_heading_part1')}{" "}
              <span className="text-gold">{t('cta.custom_heading_part2')}</span>
            </>
          }
          customSubheading={t('cta.custom_subheading')}
          customBenefits={[
            t('cta.custom_benefits.0'),
            t('cta.custom_benefits.1'),
            t('cta.custom_benefits.2'),
            t('cta.custom_benefits.3'),
          ]}
        />
      </main>

    </div>
  );
}
